import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { 
  Users2, Link2, Copy, Check, Loader2, X, Plus, 
  Crown, UserPlus, LogOut, Trash2
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

// Watch Party Create/Join Modal
export const WatchPartyModal = ({ eventId, eventTitle, isOpen, onClose, onPartyJoined }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('select'); // 'select', 'create', 'join'
  const [partyName, setPartyName] = useState('');
  const [partyCode, setPartyCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const handleCreate = async () => {
    if (!partyName.trim()) {
      setError('Please enter a party name');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('livestreamToken');
      const response = await axios.post(
        `${API}/api/livestream/watch-party/create`,
        {
          event_id: eventId,
          party_name: partyName.trim(),
          max_participants: 10
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (onPartyJoined) {
        onPartyJoined(response.data);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create watch party');
    } finally {
      setLoading(false);
    }
  };
  
  const handleJoin = async () => {
    if (!partyCode.trim()) {
      setError('Please enter a party code');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('livestreamToken');
      
      // First check party details
      const checkResponse = await axios.get(
        `${API}/api/livestream/watch-party/${partyCode.trim().toUpperCase()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (checkResponse.data.requires_ticket) {
        // Redirect to buy ticket
        setError('You need a ticket for this event to join the watch party');
        return;
      }
      
      // Join the party
      await axios.post(
        `${API}/api/livestream/watch-party/${partyCode.trim().toUpperCase()}/join`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (onPartyJoined) {
        onPartyJoined({ party_code: partyCode.trim().toUpperCase() });
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to join watch party');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Users2 className="h-5 w-5 text-purple-400" />
            Watch Party
          </DialogTitle>
          <DialogDescription className="text-gray-300">
            Watch together with friends
          </DialogDescription>
        </DialogHeader>
        
        {mode === 'select' && (
          <div className="space-y-4">
            <Button
              onClick={() => setMode('create')}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Watch Party
            </Button>
            
            <Button
              onClick={() => setMode('join')}
              variant="outline"
              className="w-full border-gray-600 text-white hover:bg-gray-800"
            >
              <Link2 className="h-4 w-4 mr-2" />
              Join with Code
            </Button>
          </div>
        )}
        
        {mode === 'create' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Party Name</label>
              <Input
                placeholder="e.g., The Squad's Stream Night"
                value={partyName}
                onChange={(e) => setPartyName(e.target.value)}
                className="bg-gray-800 border-gray-700"
              />
            </div>
            
            {error && (
              <p className="text-red-400 text-sm">{error}</p>
            )}
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => { setMode('select'); setError(''); }}
                className="flex-1 border-gray-700"
              >
                Back
              </Button>
              <Button
                onClick={handleCreate}
                disabled={loading}
                className="flex-1 bg-purple-600 hover:bg-purple-500"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
              </Button>
            </div>
          </div>
        )}
        
        {mode === 'join' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Party Code</label>
              <Input
                placeholder="Enter 8-character code"
                value={partyCode}
                onChange={(e) => setPartyCode(e.target.value.toUpperCase())}
                className="bg-gray-800 border-gray-700 uppercase tracking-widest text-center font-mono"
                maxLength={8}
              />
            </div>
            
            {error && (
              <p className="text-red-400 text-sm">{error}</p>
            )}
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => { setMode('select'); setError(''); }}
                className="flex-1 border-gray-700"
              >
                Back
              </Button>
              <Button
                onClick={handleJoin}
                disabled={loading}
                className="flex-1 bg-purple-600 hover:bg-purple-500"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Join'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Watch Party Info Panel (shown when in a party)
export const WatchPartyPanel = ({ partyCode, onLeave }) => {
  const [party, setParty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  
  const fetchParty = async () => {
    try {
      const token = localStorage.getItem('livestreamToken');
      const response = await axios.get(
        `${API}/api/livestream/watch-party/${partyCode}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setParty(response.data.party);
    } catch (err) {
      console.error('Error fetching party:', err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (partyCode) {
      fetchParty();
      const interval = setInterval(fetchParty, 5000); // Refresh every 5 seconds
      return () => clearInterval(interval);
    }
  }, [partyCode]);
  
  const copyLink = () => {
    const link = `${window.location.origin}/live/watch-party/${partyCode}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    
    setInviting(true);
    try {
      const token = localStorage.getItem('livestreamToken');
      await axios.post(
        `${API}/api/livestream/watch-party/${partyCode}/invite`,
        { email: inviteEmail.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInviteEmail('');
      setShowInvite(false);
      alert('Invitation sent!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };
  
  const handleLeave = async () => {
    if (!confirm('Are you sure you want to leave the watch party?')) return;
    
    try {
      const token = localStorage.getItem('livestreamToken');
      await axios.post(
        `${API}/api/livestream/watch-party/${partyCode}/leave`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onLeave) onLeave();
    } catch (err) {
      // If host tries to leave, they need to end the party
      if (err.response?.data?.detail?.includes('Host cannot leave')) {
        if (confirm('As host, leaving will end the party for everyone. Continue?')) {
          try {
            await axios.delete(
              `${API}/api/livestream/watch-party/${partyCode}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (onLeave) onLeave();
          } catch (e) {
            alert('Failed to end party');
          }
        }
      } else {
        alert(err.response?.data?.detail || 'Failed to leave party');
      }
    }
  };
  
  if (loading) {
    return (
      <div className="bg-purple-900/20 rounded-xl p-4 border border-purple-500/30">
        <Loader2 className="h-5 w-5 animate-spin mx-auto" />
      </div>
    );
  }
  
  if (!party) return null;
  
  const customer = JSON.parse(localStorage.getItem('livestreamCustomer') || '{}');
  const isHost = party.host_id === customer.id;
  
  return (
    <div className="bg-purple-900/20 rounded-xl p-4 border border-purple-500/30">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users2 className="h-5 w-5 text-purple-400" />
          <span className="font-semibold">{party.party_name}</span>
        </div>
        <Badge className="bg-purple-600/30 text-purple-300 text-xs">
          {party.participants?.length || 0}/{party.max_participants}
        </Badge>
      </div>
      
      {/* Party Code */}
      <div className="flex items-center gap-2 mb-4">
        <div className="flex-1 bg-black/30 rounded-lg px-3 py-2 font-mono text-center tracking-widest">
          {partyCode}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={copyLink}
          className="border-purple-500/30"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
      
      {/* Participants */}
      <div className="space-y-2 mb-4">
        <p className="text-xs text-gray-400">Watching with:</p>
        <div className="flex flex-wrap gap-2">
          {party.participants?.map((p, i) => (
            <div 
              key={i}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                p.is_host 
                  ? 'bg-yellow-500/20 text-yellow-300' 
                  : 'bg-gray-800 text-gray-300'
              }`}
            >
              {p.is_host && <Crown className="h-3 w-3" />}
              {p.name}
            </div>
          ))}
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex gap-2">
        {isHost && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowInvite(!showInvite)}
            className="flex-1 border-purple-500/30 text-purple-300"
          >
            <UserPlus className="h-4 w-4 mr-1" />
            Invite
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={handleLeave}
          className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
        >
          <LogOut className="h-4 w-4 mr-1" />
          {isHost ? 'End' : 'Leave'}
        </Button>
      </div>
      
      {/* Invite Input */}
      {showInvite && (
        <div className="mt-3 flex gap-2">
          <Input
            placeholder="Email to invite"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            className="bg-black/30 border-purple-500/30 text-sm"
          />
          <Button
            size="sm"
            onClick={handleInvite}
            disabled={inviting}
            className="bg-purple-600 hover:bg-purple-500"
          >
            {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default WatchPartyModal;
